import React, { useEffect, useMemo, useState } from 'react';
import NowName from '../components/shared/NowName';
import { useNavigate } from 'react-router-dom';
import { FileText, Lock } from 'lucide-react';
import SearchSelect from '../components/shared/SearchSelect';
import { Field, FilePicker, inputCls } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import GroupedEngagementPicker from '../components/workflow/GroupedEngagementPicker';
import QcReviewerPicks, {
  planFor, emptyReviewerPicks, reviewerPickGaps, appendReviewerPicks,
} from '../components/workflow/QcReviewerPicks';
import { engagementLabelsOf } from '../components/workflow/CoverageDisplay';
import RecordBlockers, { recordBlockReasons } from '../components/workflow/RecordBlockers';
import { formatDate, formatDateShort } from '../utils/dateFormat';
import { fetchJSON } from '../utils/api';
import { QcAnswersForm } from './WorkflowQcNew';
import { qcAnswerGaps, qcSplitGaps, feeAnswersChanged } from './qcQuestions';
import { eqrLabel } from './qcAddendum';
import { subsectionTitle, subsectionTitleBar } from '../components/workflow/constants';

// The QCA change form — shared by the New page and the Detail page's Modify
// modal. The record is picked once and locked; the amendable fields are the
// covered ENGAGEMENTS (same client group, add-only), the acceptance answers
// and remarks. The QC type and client group never change here.
//
// **沒有 FYE 欄位**——QC6/7 的 FYE 跟著連結的 engagement 走,
// 這裡只給唯讀晶片並指路到 engagement。也沒有「替換簽署 QC 文件」
// （新記錄一律是問卷）；舊的 file 記錄仍看得到自己的文件,只是不能
// 從這裡換掉。
//
// 涵蓋挑的是「案子」不是「公司」。若挑公司,加進來那間公司的合夥人不在審核者
// 池裡（池子是從涵蓋案推導的）——別人替他的案子背書,而他連信都收不到。挑案
// 之後「誰的案子被碰到誰就簽」才成立,核准也才蓋得到 engagement 上。
export const QcaChangeForm = ({ record, form, setForm, engagements = [], coveredEngagementIds = [], current = null }) => {
  const isForm = record.submissionMethod === 'form';
  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const added = (form.engagementIds || []).filter((id) => !coveredEngagementIds.includes(id));
  const removed = coveredEngagementIds.filter((id) => !(form.engagementIds || []).includes(id));
  // 提示句用短名（公司段；同公司多案時加服務·描述，engagementLabelsOf）——完整組合名在挑選器的 chips 已經看得到。
  const labels = engagementLabelsOf(engagements);
  const nameOf = (id) => (labels[id] ? labels[id].text : `#${id}`);
  return (
    <>
      <Field label="Covered Engagements">
        <GroupedEngagementPicker
          engagements={engagements}
          selectedIds={form.engagementIds || []}
          onChange={(ids) => setField('engagementIds', ids)}
          scope="all"
        />
        {added.length > 0 && (
          <p className="text-xs text-amber-700 mt-2">
            Joining this QC: {added.map(nameOf).join(', ')} — its partner must approve.
          </p>
        )}
        {removed.length > 0 && (
          <p className="text-xs text-red-700 mt-2">
            Leaving this QC: {removed.map(nameOf).join(', ')} — its partner must approve, and the engagement loses this QC6 / QC7 form on approval.
          </p>
        )}
      </Field>
      <Field label="Financial Year End">
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
          <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" title="Follows the linked engagement" />
          <span className="text-sm font-semibold text-slate-900">{formatDate(record.yearEnd)}</span>
        </div>
      </Field>
      {isForm ? (
        <div className="space-y-2">
          <h4 className={subsectionTitle}>
            <span className={subsectionTitleBar} aria-hidden="true" />
            QC6 / QC7 Form (Edit the Answers to Correct)
          </h4>
          {record.legacyImport && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              This record was imported from a signed form — only the answers the system acts on are filled in.
              Complete the whole QC6 / QC7 form here; on approval the record becomes a fully answered one and the
              uploaded signed form stays on file as the previous version.
            </p>
          )}
          {/* The Conclusion's rating, its reason and the EQR are addendum-only
              once approved — the rows print locked below; this line says what
              the record currently carries when an addendum moved them. */}
          <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
            The risk rating, its reason and the engagement quality reviewer change through a QC6 / QC7 Addendum, not here.
            {current && (current.risk || current.eqr) && (
              <>
                {' '}Current: risk rating <span className="font-semibold">{current.risk?.rating || '—'}</span>
                {current.risk?.revisedBy ? ` (revised by ${current.risk.revisedBy})` : ''}, EQR <span className="font-semibold"><NowName value={eqrLabel(current.eqr?.eqr)} now={current.eqr?.eqrNow} /></span>
                {current.eqr?.revisedBy ? ` (revised by ${current.eqr.revisedBy})` : ''}.
              </>
            )}
          </p>
          <QcAnswersForm
            qcType={record.qcType}
            answers={form.answers || {}}
            onChange={(a) => setField('answers', a)}
            engagements={(form.engagementIds || []).map((id) => engagements.find((e) => e.id === id)).filter(Boolean)}
            addendumOnly
            feePerEngagement={feeAnswersChanged(record.answers, form.answers, record.qcType)}
          />
        </div>
      ) : (
        <Field label="Signed QC Document">
          <div className="space-y-1.5">
            {(record.files || []).filter((f) => f.docType === 'QC_FORM' && !f.superseded).map((f) => (
              <p key={f.id} className="text-xs text-slate-500 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> Current: {f.originalName}
              </p>
            ))}
            <p className="text-xs text-slate-500">
              Replacing the signed document retired with the upload method — this record predates it. Correct the covered
              companies or remarks here; a new QC6 / QC7 form goes through a QC6 / QC7 Forms request.
            </p>
          </div>
        </Field>
      )}
      <Field label="Remarks">
        <textarea rows="2" value={form.remarks} onChange={(e) => setField('remarks', e.target.value)} className={inputCls} />
      </Field>
    </>
  );
};

// The picker names the RECORD (type · company · FYE) — not the application
// that produced it: a QC Amendment edits the record in place, so the
// original request no longer describes what the picker hands over. The one
// tag kept is "imported" — a record filed from a signed paper form has only
// its downstream answers, which changes what an amendment or addendum
// against it must do.
export const recordOptionLabel = (r) => {
  const covered = (r.entities || []).length > 1
    ? `${r.clientGroup || r.entityName} · ${r.entities.length} companies`
    : r.entityName;
  return `${r.qcType} · ${covered} — FYE ${formatDateShort(r.yearEnd)}${r.signedFormPending ? ' (imported, signed form pending)' : r.legacyImport ? ' (imported)' : ''}`;
};

const WorkflowQcaNew = () => {
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [recordId, setRecordId] = useState('');
  const [record, setRecord] = useState(null);       // full record (answers + files)
  const [coveredEngagementIds, setCoveredEngagementIds] = useState([]);
  // Why the picked record cannot be amended right now (closed engagement, a
  // request in flight) + its current rating / EQR — said under the picker.
  const [recordMeta, setRecordMeta] = useState(null);
  const [form, setForm] = useState({ engagementIds: [], answers: {}, remarks: '' });
  const [supportFiles, setSupportFiles] = useState([]);
  const [reason, setReason] = useState('');
  const [picks, setPicks] = useState(() => emptyReviewerPicks());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // Who reviews follows the form the amendment leaves behind (the answers as
  // edited here); a record that never answered the routing questions takes
  // the lenient reading (Partner-only).
  const plan = useMemo(
    () => (record ? planFor(form.answers, record.qcType, { lenient: true }) : null),
    [record, form.answers]
  );

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/qca/options');
        setRecords(data.records || []);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  // Picking a record loads it in full and prefills the amendable fields.
  useEffect(() => {
    if (!recordId) { setRecord(null); setRecordMeta(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(`/api/workflow/qca/record/${recordId}`);
        if (cancelled) return;
        setRecord(data.record);
        setRecordMeta({
          engagementLock: data.engagementLock || null,
          openRequests: data.openRequests || null,
          current: { risk: data.risk || null, eqr: data.eqr || null },
        });
        setCoveredEngagementIds(data.coveredEngagementIds || []);
        // 挑選器的候選跟著它所屬的記錄走，不寄放在兄弟流程的
        // options 裡。這份 feed 是**案子**：記錄現有的涵蓋案（鎖住，
        // 只能加不能減）＋同集團還沒有 QC 的案。
        setEngagements(data.engagements || []);
        setForm({
          engagementIds: data.coveredEngagementIds || [],
          answers: data.record.answers || {},
          remarks: data.record.remarks || '',
        });
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [recordId]);

  // Everything still empty, keyed to the control it belongs to — after the
  // first Submit the page marks those controls and lists them in the banner.
  const gaps = useMemo(() => {
    const g = [];
    if (!record) g.push({ key: 'qcRecord', label: 'QC Record' });
    if (record) g.push(...reviewerPickGaps(plan, picks));
    if (!reason.trim()) g.push({ key: 'reason', label: 'Reason' });
    // An imported record must leave this amendment fully answered.
    if (record && record.legacyImport) g.push(...qcAnswerGaps(form.answers, record.qcType));
    if (record) g.push(...qcSplitGaps(form.answers, record.qcType, form.engagementIds, { requireFee: feeAnswersChanged(record.answers, form.answers, record.qcType) }));
    return g;
  }, [record, plan, picks, reason, form.answers, form.engagementIds]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setError(null);
    setSaving(true);
    try {
      const fd = new FormData();
      fd.set('qcRecordId', String(record.id));
      if ((form.engagementIds || []).length > 0) fd.set('engagementIds', JSON.stringify(form.engagementIds));
      if (record.submissionMethod === 'form') fd.set('answers', JSON.stringify(form.answers || {}));
      fd.set('remarks', form.remarks || '');
      fd.set('reason', reason.trim());
      appendReviewerPicks(fd, plan, picks);
      supportFiles.forEach((f) => fd.append('files', f));
      const data = await fetchJSON('/api/workflow/qca', { method: 'POST', body: fd });
      navigate(`/workflow/qca/${data.request.id}`);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/qca"
      backLabel="QC Amendment"
      title="New QC Amendment Request"
      subtitle="Correct an approved QC6 / QC7 record."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={!!record && !!recordMeta && recordBlockReasons({ ...recordMeta, flow: 'qca' }).length > 0}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Select the QC Record">
          <Field label="QC Record" required field="qcRecord">
            <SearchSelect
              value={recordId}
              onChange={setRecordId}
              options={records.map((r) => ({ value: r.id, label: recordOptionLabel(r) }))}
              placeholder="Search a QC record…"
            />
            {/* Every record is listed; the one that cannot be amended says
                why here (closed engagement, a request in flight). */}
            {record && recordMeta && (
              <RecordBlockers engagementLock={recordMeta.engagementLock} openRequests={recordMeta.openRequests} flow="qca" />
            )}
          </Field>
        </FormSection>

        <FormSection step={2} title="Requested Changes">
          {record ? (
            <QcaChangeForm
              record={record} form={form} setForm={setForm}
              engagements={engagements} coveredEngagementIds={coveredEngagementIds}
              current={recordMeta?.current || null}
            />
          ) : (
            <p className="text-sm text-slate-500">Select a QC record above to edit its details.</p>
          )}
        </FormSection>

        <FormSection step={3} title="Attachments">
          <Field label="Supporting Files">
            <FilePicker
              multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={(e) => setSupportFiles(Array.from(e.target.files || []))}
            />
          </Field>
        </FormSection>

        <FormSection step={4} title="Reason">
          <Field label="Reason" required field="reason">
            <textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>

        {/* The form's answers decide how many pickers show — the title follows. */}
        <FormSection step={5} title={plan && plan.stages && plan.stages.length > 1 ? 'Reviewers' : 'Reviewer'}>
          {record ? (
            <QcReviewerPicks
              plan={plan}
              picks={picks}
              onChange={setPicks}
              qcType={record.qcType}
              hint="You pick who reviews this amendment, from the firm's roster."
            />
          ) : (
            <p className="text-sm text-slate-500">Select a QC record above first.</p>
          )}
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowQcaNew;
